/**
 * Payment initiation helpers (Whish collectUrl + BLOM Unified Checkout).
 */

import BlomApi, {BlomCaptureContextPayload} from '../Api/BlomApi'
import {
  ensureBlomMountElement,
  loadUnifiedCheckoutScript,
  mountBlomCheckout,
} from '../hooks/useBLOMUnifiedCheckout'

export type PaymentInitiationParsed = {
  formActionUrl?: string
  collectUrl?: string
  formFields?: Record<string, string>
  external_id?: number | string
  captureContextJwt?: string
  clientLibraryUrl?: string
}

/** Map GatewayFactory name → vendor-api route slug (pay-{slug}). */
export function factoryNameToPaySlug(factoryName: string): string {
  const normalized = (factoryName || '').toUpperCase().trim()
  const map: Record<string, string> = {
    PY_WHISH: 'whish',
    PY_BLOM_UC: 'blom',
    PY_BLOM: 'blom',
    PY_OMT: 'omt',
  }

  if (map[normalized]) {
    return map[normalized]
  }

  return normalized.replace(/^PY_/, '').toLowerCase().split('_')[0]
}

export function isBlomUnifiedCheckout(factoryName: string): boolean {
  const normalized = (factoryName || '').toUpperCase().trim()
  return normalized === 'PY_BLOM_UC' || normalized === 'PY_BLOM'
}

/** Map display symbols / messy values to ISO-4217 for Cybersource. */
export function toIsoCurrencyCode(value?: string | null): string {
  const raw = (value || localStorage.getItem('currency') || 'USD').toString().trim()
  const upper = raw.toUpperCase()

  if (upper === '$' || upper === 'US$' || upper === 'USD$' || upper === 'DOLLAR') {
    return 'USD'
  }

  if (/^[A-Z]{3}$/.test(upper)) {
    return upper
  }

  // Lebanon B2B platform default when only a symbol is known
  return 'USD'
}

/**
 * Mode A: capture-context → UC modal → accept-payment.
 */
export function runBlomUnifiedCheckout(
  payload: BlomCaptureContextPayload,
  setIsLoading?: (v: boolean) => void,
  onError?: (message: string) => void
): void {
  const requestPayload: BlomCaptureContextPayload = {
    ...payload,
    currency_code: toIsoCurrencyCode(payload.currency_code),
  }

  BlomApi.createCaptureContext(
    requestPayload,
    async (res: any) => {
      const data = res?.data ?? res ?? {}
      const jwt = data.capture_context_jwt || data.captureContextJwt
      const libraryUrl = data.client_library_url || data.clientLibraryUrl
      const externalId = Number(data.external_id ?? data.externalId)

      if (!jwt || !libraryUrl || !externalId) {
        onError?.(String(res?.message || 'Unable to start BLOM checkout'))
        return
      }

      try {
        ensureBlomMountElement('blom-uc-mount')
        await loadUnifiedCheckoutScript(String(libraryUrl))
        const transientToken = await mountBlomCheckout(String(jwt), '#blom-uc-mount')

        BlomApi.acceptPayment(
          externalId,
          transientToken,
          (acceptRes: any) => {
            const pending = acceptRes?.data?.pending || acceptRes?.pending
            const success =
              acceptRes?.data?.confirmation === 'confirmed' ||
              acceptRes?.data?.success === true ||
              acceptRes?.success === true

            if (pending) {
              window.location.hash = `#/wallet/payment-history`
              return
            }

            if (success) {
              window.location.hash = `#/wallet/payment-success?external_id=${externalId}`
              return
            }

            onError?.(String(acceptRes?.message || 'Payment declined'))
          },
          setIsLoading
        )
      } catch (error: any) {
        onError?.(String(error?.message || 'BLOM checkout failed'))
        if (setIsLoading) {
          setIsLoading(false)
        }
      }
    },
    setIsLoading
  )
}

export function parsePaymentAmount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '').trim()
    const direct = Number(cleaned)
    if (Number.isFinite(direct)) {
      return direct
    }
    const match = cleaned.match(/-?\d+(\.\d+)?/)
    if (match) {
      const parsed = Number(match[0])
      return Number.isFinite(parsed) ? parsed : 0
    }
  }

  return 0
}

/** Display money for OTP / checkout: "$918.00" or "10.00 LBP". Never "undefined undefined". */
export function formatPaymentDisplay(amount: unknown, currency?: string | null): string {
  if (amount === null || amount === undefined || amount === '') {
    return ''
  }

  if (typeof amount === 'string') {
    const trimmed = amount.trim()
    if (trimmed && !/\bundefined\b/i.test(trimmed) && /[0-9]/.test(trimmed)) {
      const num = parsePaymentAmount(trimmed)
      const code = (currency || '').toString().trim().toUpperCase()
      if (num > 0 && code) {
        if (code === 'USD' || code === '$') {
          return `$${num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        }
        return `${num.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${code}`.trim()
      }
      if (!/\bundefined\b/i.test(trimmed)) {
        return trimmed
      }
    }
  }

  const num = parsePaymentAmount(amount)
  const code = (currency || localStorage.getItem('currency') || 'USD').toString().trim().toUpperCase()
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  if (code === 'USD' || code === '$') {
    return `$${formatted}`
  }

  return `${formatted} ${code}`.trim()
}

/** Coerce API multilingual objects / nested values to a display string. */
export function resolveLocalizedString(
  value: unknown,
  lang?: string,
  defLang?: string
): string {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  if (typeof value !== 'object') {
    return ''
  }

  const record = value as Record<string, unknown>
  const candidates = [lang, defLang, 'en', 'ar', 'ku'].filter(Boolean) as string[]

  for (const key of candidates) {
    const hit = record[key]
    if (typeof hit === 'string' || typeof hit === 'number') {
      return String(hit)
    }
    if (hit && typeof hit === 'object') {
      const nested = resolveLocalizedString(hit, lang, defLang)
      if (nested) {
        return nested
      }
    }
  }

  const first = Object.values(record)[0]
  if (typeof first === 'string' || typeof first === 'number') {
    return String(first)
  }
  if (first && typeof first === 'object') {
    return resolveLocalizedString(first, lang, defLang)
  }

  return ''
}

export function parsePaymentResponse(data: any): PaymentInitiationParsed {
  const payload = data?.data ?? data ?? {}

  const formFieldsRaw = payload.form_fields || payload.formFields || {}
  const formFields: Record<string, string> = {}

  if (formFieldsRaw && typeof formFieldsRaw === 'object') {
    Object.entries(formFieldsRaw).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        return
      }
      formFields[key] = String(value)
    })
  }

  return {
    formActionUrl: payload.form_action_url || payload.formActionUrl,
    collectUrl: payload.collectUrl || payload.collect_url,
    formFields: Object.keys(formFields).length > 0 ? formFields : undefined,
    external_id: payload.external_id ?? payload.externalId,
    captureContextJwt: payload.capture_context_jwt || payload.captureContextJwt,
    clientLibraryUrl: payload.client_library_url || payload.clientLibraryUrl,
  }
}

/** Build a hidden form and auto-submit (non-BLOM legacy only). */
export function submitPaymentForm(
  formActionUrl: string,
  formFields: Record<string, string>
): void {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = formActionUrl
  form.style.display = 'none'

  Object.entries(formFields).forEach(([name, value]) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  })

  document.body.appendChild(form)
  form.submit()
}

/** Whish → redirect; BLOM SA form removed (use runBlomUnifiedCheckout). */
export function handlePaymentInitiationResponse(data: any): boolean {
  const parsed = parsePaymentResponse(data)

  if (parsed.captureContextJwt && parsed.clientLibraryUrl) {
    return false
  }

  if (parsed.collectUrl) {
    window.location.href = parsed.collectUrl
    return true
  }

  // Do not auto-submit Secure Acceptance forms for BLOM anymore.
  if (
    parsed.formActionUrl &&
    parsed.formFields &&
    !String(parsed.formActionUrl).includes('secureacceptance')
  ) {
    submitPaymentForm(parsed.formActionUrl, parsed.formFields)
    return true
  }

  return false
}
